import React, { useEffect, useMemo, useState } from "react";
import api from "../../api/axios";
import "./Dashboard.css";

const Dashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [year] = useState(new Date().getFullYear());

  const fetchDashboard = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await api.get(`/api/admin/dashboard?year=${year}`);
      setData(res.data);
    } catch (err) {
      setError("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year]);

  const stats = data?.stats || {};
  const charts = data?.charts || {};
  const activity = data?.recentActivity || {};

  const revenueBars = useMemo(() => {
    const monthlyRevenue = charts.monthlyRevenue || [];
    const max = monthlyRevenue.reduce((acc, item) => Math.max(acc, item.amount ?? 0), 0);
    return monthlyRevenue.map((item) => ({
      label: item.label ?? "",
      height: max > 0 ? Math.round(((item.amount ?? 0) / max) * 100) : 0,
      value: item.amount ?? 0,
    }));
  }, [charts.monthlyRevenue]);

  const growthBars = useMemo(() => {
    const userGrowth = charts.userGrowth || [];
    const max = userGrowth.reduce((acc, item) => Math.max(acc, item.count ?? 0), 0);
    return userGrowth.map((item) => ({
      label: item.label ?? "",
      height: max > 0 ? Math.round(((item.count ?? 0) / max) * 100) : 0,
      value: item.count ?? 0,
    }));
  }, [charts.userGrowth]);

  if (loading && !data) {
    return <div className="p-4">Loading Dashboard...</div>;
  }

  return (
    <div className="dashboard-home">
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
        <div>
          <h2 className="page-title mb-1">Platform Overview</h2>
          <p className="text-gray small">Unified Multi-Company Administration</p>
        </div>
        <div className="d-flex gap-2">
          <button className="btn btn-outline-secondary btn-sm" onClick={fetchDashboard}>
            {loading ? "Refreshing..." : "Refresh"}
          </button>
        </div>
      </div>

      {error && <div className="alert alert-danger mt-3 mb-0">{error}</div>}

      {/* --- Stats Cards Row --- */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon blue-bg">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="3" width="7" height="7"></rect>
              <rect x="14" y="3" width="7" height="7"></rect>
              <rect x="14" y="14" width="7" height="7"></rect>
              <rect x="3" y="14" width="7" height="7"></rect>
            </svg>
          </div>
          <div className="stat-info">
            <h4>Total Companies</h4>
            <div className="stat-value">{stats.totalCompanies || 0}</div>
            <span className="stat-sub text-gray">Active sister companies</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon green-bg">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
              <circle cx="9" cy="7" r="4"></circle>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
              <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
            </svg>
          </div>
          <div className="stat-info">
            <h4>Active Members</h4>
            <div className="stat-value">{stats.activeUsers || 0}</div>
            <span className="stat-sub text-gray">Approved roles base</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon purple-bg">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="1" x2="12" y2="23"></line>
              <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
            </svg>
          </div>
          <div className="stat-info">
            <h4>Global Revenue (MTD)</h4>
            <div className="stat-value">${Number(stats.commissionsMonth || 0).toLocaleString()}</div>
            <span className="stat-sub text-purple">Platform earnings</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon red-bg">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 12V7H5a2 2 0 0 1 0-4h14v4"></path>
              <path d="M3 5v14a2 2 0 0 0 2 2h16v-5"></path>
              <path d="M18 12a2 2 0 0 0 0 4h4v-4Z"></path>
            </svg>
          </div>
          <div className="stat-info">
            <h4>Wallet Liability</h4>
            <div className="stat-value">{Number(stats.walletLiability || 0).toLocaleString()}</div>
            <span className="stat-sub text-red">Total user points</span>
          </div>
        </div>
      </div>

      {/* --- Charts Section --- */}
      <div className="content-grid mb-4">
        <div className="content-card">
          <div className="card-header">
            <h3>Monthly Commission Points</h3>
          </div>
          <div className="chart-wrapper">
            <div className="y-axis-title">Revenue ($)</div>
            <div className="chart-container">
              <div className="y-axis">
                <span>Max</span>
                <span>75%</span>
                <span>50%</span>
                <span>25%</span>
                <span>0</span>
              </div>
              <div className="bars-container">
                {revenueBars.map((data, index) => (
                  <div key={index} className="bar-group">
                    <div className="bar" style={{ height: `${data.height}%` }} title={`$${data.value}`}></div>
                    <span className="x-label">{data.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="content-card">
          <div className="card-header">
            <h3>User Growth</h3>
          </div>
          <div className="chart-wrapper">
            <div className="y-axis-title">New Users</div>
            <div className="chart-container">
              <div className="y-axis">
                <span>Max</span>
                <span>75%</span>
                <span>50%</span>
                <span>25%</span>
                <span>0</span>
              </div>
              <div className="bars-container">
                {growthBars.map((data, index) => (
                  <div key={index} className="bar-group">
                    <div className="bar green-bar" style={{ height: `${data.height}%`, background: 'linear-gradient(to top, #059669, #34d399)' }} title={`${data.value} Users`}></div>
                    <span className="x-label">{data.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* --- Recent Activity Section --- */}
      <div className="content-grid">
        <div className="content-card">
          <div className="card-header">
            <h3>Pending Approvals</h3>
            <span className="badge bg-warning text-dark">Action Required</span>
          </div>
          <div className="table-responsive">
            <table className="table table-sm table-hover align-middle">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Company</th>
                  <th>Role</th>
                  <th>Requested</th>
                </tr>
              </thead>
              <tbody>
                {activity.pendingApprovals?.length > 0 ? (
                  activity.pendingApprovals.map((req) => (
                    <tr key={req.id}>
                      <td>
                        <div className="fw-bold small">{req.user?.name}</div>
                        <div className="text-gray smaller">{req.user?.email}</div>
                      </td>
                      <td className="small">{req.company?.name}</td>
                      <td className="small">
                         <span className="px-2 py-1 bg-light border rounded">{req.role?.name}</span>
                      </td>
                      <td className="smaller text-gray">
                        {new Date(req.created_at).toLocaleDateString()}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr><td colSpan="4" className="text-center py-4 text-muted small">No pending requests</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="content-card">
          <div className="card-header">
            <h3>Recent Commissions</h3>
            <span className="text-gray small">Live Feed</span>
          </div>
          <div className="table-responsive">
             <table className="table table-sm table-hover align-middle">
              <thead>
                <tr>
                  <th>Member</th>
                  <th>Role</th>
                  <th>Amount</th>
                </tr>
              </thead>
              <tbody>
                {activity.recentCommissions?.length > 0 ? (
                  activity.recentCommissions.map((comm) => (
                    <tr key={comm.id}>
                      <td className="small">{comm.user_name}</td>
                      <td className="smaller text-gray">{comm.role_name}</td>
                      <td className="fw-bold text-success small">+{comm.amount} pts</td>
                    </tr>
                  ))
                ) : (
                  <tr><td colSpan="3" className="text-center py-4 text-muted small">No recent earnings</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
